import { useEffect, useMemo, useState } from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import {
  AlertTriangle,
  Archive,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  Inbox,
  Monitor,
  Moon,
  Settings,
  Sun,
  User,
} from 'lucide-react';

import type { Viewer } from '@beacon/core';

import { AdminAutomationsList } from '@/features/admin/AdminAutomationsList.js';
import { AdminClientsList } from '@/features/admin/AdminClientsList.js';
import ContractWizard from '@/features/admin/ContractWizard.js';
import { AdminTemplatesList } from '@/features/admin/AdminTemplatesList.js';
import { AdminUsersList } from '@/features/admin/AdminUsersList.js';
import { SettingsHub } from '@/features/admin/SettingsHub.js';
import { ContractDetail } from '@/features/admin/ContractDetail.js';
import { OrganizationDetail } from '@/features/admin/OrganizationDetail.js';
import { Login } from '@/features/auth/Login.js';
import { ClientHome } from '@/features/portal/ClientHome.js';
import { TriageInbox } from '@/features/triage/TriageInbox.js';
import { UnknownSenders } from '@/features/triage/UnknownSenders.js';
import { TicketDetail } from '@/features/ticket/TicketDetail.js';
import { ReportsQueue } from '@/features/reports/ReportsQueue.js';
import { ReportDetail } from '@/features/reports/ReportDetail.js';
import { ReportsHistory } from '@/features/reports/ReportsHistory.js';
import { SLAPanelsReference } from '@/features/reference/SLAPanelsReference.js';
import { Avatar } from '@/components/beacon/avatar.js';
import { CommandPalette } from '@/components/ui/command-palette.js';
import { KeyboardCheatSheet } from '@/components/ui/keyboard-cheat-sheet.js';
import { useContracts, useContractsById } from '@/hooks/use-contracts.js';
import { useHotkeys } from '@/hooks/use-hotkeys.js';
import { useNow } from '@/hooks/use-now.js';
import { useTickets } from '@/hooks/use-tickets.js';
import { useUnknownEmails } from '@/hooks/use-unknown-emails.js';
import { useViewer } from '@/hooks/use-viewer.js';
import { cn } from '@/lib/cn.js';
import { slaSnapshotFor } from '@/lib/sla-display.js';

export function App() {
  const { pathname } = useLocation();
  const isPublic = pathname === '/login';
  const viewerQuery = useViewer();
  const [cheatSheetOpen, setCheatSheetOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [theme, setTheme] = useTheme();

  // Global hotkeys:
  //   ⌘K → command palette
  //   ⌘? → cheat-sheet (cmd+shift+/ since ? is shift-/ on most layouts)
  useHotkeys({
    'cmd+k': () => {
      // Toggle: if palette is already open, close it; else open it
      // (and close the cheat-sheet so we never stack modals).
      setPaletteOpen((v) => !v);
      setCheatSheetOpen(false);
    },
    'cmd+/': () => setCheatSheetOpen((v) => !v),
    'cmd+shift+/': () => setCheatSheetOpen((v) => !v),
  });

  function toggleTheme() {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }

  if (isPublic) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
      </Routes>
    );
  }

  // Block render until we know who the viewer is — the sidebar's nav
  // and the default route both branch on it. Brief; the query has a
  // 30-min stale time so this only flickers on first paint.
  if (viewerQuery.isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-ink-900 text-[13px] text-ink-300">
        Loading…
      </div>
    );
  }
  const viewer = viewerQuery.data;
  const isClient = viewer?.organizationType === 'client';
  const homePath = isClient ? '/home' : '/inbox';

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-ink-900">
      <TopBar
        viewer={viewer}
        isClient={isClient}
        theme={theme}
        setTheme={setTheme}
      />
      <div className="flex-1 min-h-0 flex">
        {!isClient && <Sidebar />}
        <main className="flex-1 min-w-0 overflow-hidden">
          <Routes>
            <Route path="/" element={<Navigate to={homePath} replace />} />
            {/* Client portal */}
            <Route path="/home" element={<ClientHome />} />
            <Route path="/tickets" element={<TriageInbox />} />
            {/* Shared screens — data layer org-scopes */}
            <Route path="/inbox" element={<TriageInbox />} />
            <Route
              path="/inbox/unknown"
              element={
                isClient ? (
                  <Navigate to="/home" replace />
                ) : (
                  <UnknownSenders />
                )
              }
            />
            <Route
              path="/internal"
              element={<TriageInbox scope="internal" />}
            />
            <Route path="/tickets/:number" element={<TicketDetail />} />
            <Route path="/reports" element={<ReportsQueue />} />
            <Route path="/reports/history" element={<ReportsHistory />} />
            <Route path="/reports/:id" element={<ReportDetail />} />
            {/* Admin — internal only. Clients hitting these get redirected
                home; the API itself also 403s. */}
            <Route
              path="/admin/*"
              element={
                isClient ? (
                  <Navigate to="/home" replace />
                ) : (
                  <AdminRoutes />
                )
              }
            />
            <Route path="/reference/sla" element={<SLAPanelsReference />} />
            <Route
              path="*"
              element={
                <div className="p-8 text-ink-300">
                  Not found.{' '}
                  <Link to={homePath} className="text-beacon-300 hover:underline">
                    Back to {isClient ? 'home' : 'inbox'}
                  </Link>
                </div>
              }
            />
          </Routes>
        </main>
      </div>
      <KeyboardCheatSheet
        open={cheatSheetOpen}
        onClose={() => setCheatSheetOpen(false)}
      />
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onOpenCheatSheet={() => setCheatSheetOpen(true)}
        onToggleTheme={toggleTheme}
      />
    </div>
  );
}

/** Nested routes for the admin section. Mounted under /admin/* on the
 *  parent route, gated to internal viewers. */
function AdminRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="clients" replace />} />
      <Route path="clients" element={<AdminClientsList />} />
      <Route path="clients/new-contract" element={<ContractWizard />} />
      <Route path="clients/:orgId" element={<OrganizationDetail />} />
      <Route
        path="clients/:orgId/contracts/:contractId"
        element={<ContractDetail />}
      />
      <Route path="users" element={<AdminUsersList />} />
      <Route path="automations" element={<AdminAutomationsList />} />
      <Route path="templates" element={<AdminTemplatesList />} />
      <Route path="settings" element={<SettingsHub />} />
    </Routes>
  );
}

/* ----------------------------------------------------------------
 * Top bar — global chrome.
 *
 * Lighthouse mark + wordmark (brand anchor, Decision #12), theme
 * switcher (Decision #15 — light theme is table-stakes secondary),
 * AEST/A$ pill, viewer avatar. Stays present on portal screens too;
 * the sidebar is what we drop for clients (Decision #14).
 * ---------------------------------------------------------------- */
interface TopBarProps {
  viewer: Viewer | null | undefined;
  isClient: boolean;
  theme: Theme;
  setTheme: (t: Theme) => void;
}

function TopBar({ viewer, isClient, theme, setTheme }: TopBarProps) {
  const wordmark = isClient ? 'Conduct HQ' : 'Beacon';
  const subtitle = isClient ? 'support' : 'ops';
  return (
    <header className="h-12 shrink-0 border-b border-ink-600 bg-ink-900 flex items-center px-4 gap-3">
      <Link to="/" className="flex items-center gap-2 focus-ring rounded-md">
        <span className="w-7 h-7 rounded bg-beacon-400 text-ink-900 inline-flex items-center justify-center">
          <LighthouseIcon size={16} />
        </span>
        <div className="leading-tight">
          <div className="text-[13px] font-semibold tracking-tight text-ink-100">
            {wordmark}
          </div>
          <div className="text-[10px] text-ink-300 mono -mt-0.5 uppercase tracking-[0.08em]">
            {subtitle}
          </div>
        </div>
      </Link>

      <div className="ml-auto flex items-center gap-2">
        <ThemeSwitcher theme={theme} onChange={setTheme} />
        <span className="text-[11.5px] text-ink-300 mono px-2 hidden md:inline">
          Australia/Sydney · A$
        </span>
        {viewer && (
          <Avatar name={viewer.name} size={26} />
        )}
      </div>
    </header>
  );
}

function ThemeSwitcher({
  theme,
  onChange,
}: {
  theme: Theme;
  onChange: (t: Theme) => void;
}) {
  const swatch = (id: Theme, icon: React.ReactNode, label: string, disabled = false) => (
    <button
      key={id}
      onClick={() => !disabled && onChange(id)}
      disabled={disabled}
      title={disabled ? `${label} (coming soon)` : label}
      aria-label={label}
      aria-pressed={theme === id}
      className={cn(
        'h-6 w-6 inline-flex items-center justify-center rounded-[4px] transition-colors',
        theme === id
          ? 'bg-ink-700 text-ink-100'
          : disabled
            ? 'text-ink-400 cursor-not-allowed'
            : 'text-ink-300 hover:text-ink-100'
      )}
    >
      {icon}
    </button>
  );
  return (
    <div className="flex items-center bg-ink-800 border border-ink-600 rounded-md p-0.5">
      {swatch('light', <Sun size={13} />, 'Light')}
      {swatch('dark', <Moon size={13} />, 'Dark')}
      {swatch('system', <Monitor size={13} />, 'System', true)}
    </div>
  );
}

/* ----------------------------------------------------------------
 * Lighthouse mark — inlined SVG. Lucide doesn't ship one and this
 * is the brand anchor (Decision #12). Path matches the design
 * handoff icon so the visual is identical across reference and app.
 * ---------------------------------------------------------------- */
function LighthouseIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2 10 6h4Z" />
      <path d="M9 6h6l1 4H8Z" />
      <path d="M8 10h8l1 12H7Z" />
      <path d="M2 14h4M18 14h4M3 19h3M18 19h3" />
    </svg>
  );
}

/* ----------------------------------------------------------------
 * Theme — stored in localStorage; data-theme attribute on <html>
 * drives the ink-* token swap defined in globals.css. 'system' is
 * stubbed off until we wire prefers-color-scheme.
 * ---------------------------------------------------------------- */
type Theme = 'light' | 'dark' | 'system';

function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved =
      typeof window !== 'undefined'
        ? window.localStorage.getItem('beacon-theme')
        : null;
    return saved === 'light' || saved === 'dark' ? saved : 'dark';
  });
  useEffect(() => {
    const effective = theme === 'system' ? 'dark' : theme;
    document.documentElement.setAttribute('data-theme', effective);
    window.localStorage.setItem('beacon-theme', theme);
  }, [theme]);
  return [theme, setTheme];
}

/* ----------------------------------------------------------------
 * Sidebar — staff-only. Matches design handoff IA:
 *   WORK: Inbox, Breaching SLA, Assigned to me, Awaiting client,
 *         Resolved, Internal board, Unknown senders
 *   CLIENTS: Dynamic contract list with logos + open ticket counts
 *   REPORTS: Month-specific, Archive, Contracts link
 *   Admin: Compact gear row at bottom (rarely used, not daily nav)
 * Bottom-anchored SLA pulse card (Components.md "Sidebar" callout).
 * ---------------------------------------------------------------- */
function Sidebar() {
  const { pathname, search } = useLocation();
  const fullLocation = pathname + search;
  const viewerQuery = useViewer();
  const viewer = viewerQuery.data;
  const ticketsQuery = useTickets();
  const tickets = ticketsQuery.data ?? [];
  const contractsQuery = useContracts();
  const contracts = contractsQuery.data ?? [];
  const contractsById = useContractsById();
  const now = useNow(60_000);
  const unknownEmailsQuery = useUnknownEmails('pending');
  const unknownCount = unknownEmailsQuery.data?.length ?? 0;
  const [adminExpanded, setAdminExpanded] = useState(false);

  const counts = useMemo(() => {
    let breaching = 0;
    let warn = 0;
    let ok = 0;
    let awaiting = 0;
    let inboxOpen = 0;
    let internalOpen = 0;
    let assignedToMe = 0;
    let resolved = 0;
    for (const t of tickets) {
      if (t.status === 'resolved') {
        resolved += 1;
        continue;
      }
      if (t.status === 'closed') continue;
      // Open ticket
      if (t.status === 'awaiting_client') awaiting += 1;
      if (t.assigneeUserId === viewer?.userId) assignedToMe += 1;
      const contract = contractsById.get(t.contractId);
      if (contract?.type === 'internal') internalOpen += 1;
      else inboxOpen += 1;
      const snap = slaSnapshotFor(t, contract, now);
      if (!snap) continue;
      if (snap.state === 'breached' || snap.state === 'breaching')
        breaching += 1;
      else if (snap.state === 'warn') warn += 1;
      else if (snap.state === 'ok') ok += 1;
    }
    return { breaching, warn, ok, awaiting, inboxOpen, internalOpen, assignedToMe, resolved };
  }, [tickets, contractsById, now, viewer?.userId]);

  // Group open tickets by contract for the CLIENTS section
  const ticketsByContract = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of tickets) {
      if (t.status === 'closed' || t.status === 'resolved') continue;
      map.set(t.contractId, (map.get(t.contractId) ?? 0) + 1);
    }
    return map;
  }, [tickets]);

  // Active (non-internal) contracts sorted by open ticket count descending
  const clientContracts = useMemo(() =>
    contracts
      .filter((c) => c.type !== 'internal' && c.status === 'active')
      .sort((a, b) => (ticketsByContract.get(b.id) ?? 0) - (ticketsByContract.get(a.id) ?? 0)),
    [contracts, ticketsByContract]
  );

  // Current month label for reports section (e.g. "April 2026")
  const reportMonthLabel = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'Australia/Sydney' });
  }, []);

  return (
    <aside className="w-[220px] shrink-0 border-r border-ink-600 bg-ink-850 hidden md:flex flex-col">
      {/* WORK — high-frequency filters */}
      <div className="p-2 space-y-0.5">
        <SidebarSectionHeader>Work</SidebarSectionHeader>
        <SidebarItem
          to="/inbox"
          icon={<Inbox size={15} />}
          label="Inbox"
          count={counts.inboxOpen || undefined}
          activePath={fullLocation}
        />
        <SidebarItem
          to="/inbox?filter=urgent"
          icon={<AlertTriangle size={15} />}
          label="Breaching SLA"
          count={counts.breaching || undefined}
          tone="red"
          activePath={fullLocation}
        />
        <SidebarItem
          to="/inbox?filter=mine"
          icon={<User size={15} />}
          label="Assigned to me"
          count={counts.assignedToMe || undefined}
          activePath={fullLocation}
        />
        <SidebarItem
          to="/inbox?filter=awaiting"
          icon={<Clock size={15} />}
          label="Awaiting client"
          count={counts.awaiting || undefined}
          activePath={fullLocation}
        />
        <SidebarItem
          to="/inbox?filter=resolved"
          icon={<Check size={15} />}
          label="Resolved"
          count={counts.resolved || undefined}
          activePath={fullLocation}
        />
        <SidebarItem
          to="/internal"
          icon={<FileText size={15} />}
          label="Internal board"
          count={counts.internalOpen || undefined}
          activePath={fullLocation}
        />
        {unknownCount > 0 && (
          <SidebarItem
            to="/inbox/unknown"
            icon={<AlertTriangle size={15} />}
            label="Unknown senders"
            count={unknownCount}
            tone="amber"
            activePath={fullLocation}
          />
        )}
      </div>

      {/* CLIENTS — dynamic contract list with logos + open ticket counts */}
      <div className="p-2 space-y-0.5 min-h-0 flex-1 overflow-y-auto">
        <SidebarSectionHeader>Contracts</SidebarSectionHeader>
        {clientContracts.map((c) => {
          const openCount = ticketsByContract.get(c.id) ?? 0;
          const isActive = fullLocation.includes(`/contracts/${c.id}`);
          return (
            <Link
              key={c.id}
              to={`/admin/clients/${c.organizationId}/contracts/${c.id}`}
              className={cn(
                'w-full h-8 px-2 rounded-md text-[13px] flex items-center gap-2 transition-colors',
                isActive
                  ? 'bg-ink-700 text-ink-100'
                  : 'text-ink-200 hover:bg-ink-800'
              )}
            >
              <ContractLogo name={c.name} size={18} />
              <span className="truncate flex-1">{c.name}</span>
              {openCount > 0 && (
                <span className={cn(
                  'mono text-[10.5px] px-1 rounded',
                  isActive ? 'bg-ink-600 text-ink-100' : 'bg-ink-700 text-ink-300'
                )}>
                  {openCount}
                </span>
              )}
            </Link>
          );
        })}
      </div>

      {/* REPORTS — month-specific label + archive */}
      <div className="p-2 space-y-0.5">
        <SidebarSectionHeader>Reports</SidebarSectionHeader>
        <SidebarItem
          to="/reports"
          icon={<FileText size={15} />}
          label="Review queue"
          activePath={fullLocation}
        />
        <SidebarItem
          to="/reports/history"
          icon={<Archive size={15} />}
          label="History"
          activePath={fullLocation}
        />
      </div>

      {/* ADMIN — collapsible, rarely used daily */}
      <div className="p-2 space-y-0.5 border-t border-ink-600">
        <button
          onClick={() => setAdminExpanded((v) => !v)}
          className="w-full h-7 px-2 rounded-md text-[10.5px] uppercase tracking-[0.12em] text-ink-400 font-semibold flex items-center gap-1 hover:text-ink-300 transition-colors"
        >
          {adminExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          Admin
        </button>
        {adminExpanded && (
          <div className="space-y-0.5">
            <SidebarItem to="/admin/clients" icon={<Building2 size={15} />} label="Clients" activePath={fullLocation} />
            <SidebarItem to="/admin/users" icon={<User size={15} />} label="Users" activePath={fullLocation} />
            <SidebarItem to="/admin/settings" icon={<Settings size={15} />} label="Settings" activePath={fullLocation} />
          </div>
        )}
      </div>

      {/* SLA Pulse footer */}
      <div className="p-3 border-t border-ink-600">
        <SLAPulseCard
          breaching={counts.breaching}
          warn={counts.warn}
          ok={counts.ok}
        />
      </div>
    </aside>
  );
}

/* Contract logo — square badge with 2-letter tag derived from name. */
const CONTRACT_HUES = ['#E57D2F', '#4C8C5B', '#7A4A9D', '#2E86AB', '#D64045', '#6B8F71', '#9B5DE5', '#F15BB5'] as const;

function ContractLogo({ name, size = 24 }: { name: string; size?: number }) {
  const tag = name
    .split(/[\s-]+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const color = CONTRACT_HUES[name.length % CONTRACT_HUES.length];
  return (
    <span
      className="inline-flex items-center justify-center font-bold tracking-tight text-ink-900 shrink-0 rounded-[4px]"
      style={{ width: size, height: size, background: color, fontSize: Math.max(9, size * 0.42) }}
    >
      {tag}
    </span>
  );
}

function SidebarSectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-2 pt-2 pb-1 text-[10.5px] uppercase tracking-[0.12em] text-ink-400 font-semibold">
      {children}
    </div>
  );
}

interface SidebarItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  count?: number | string;
  tone?: 'red' | 'amber';
  activePath: string;
}

function SidebarItem({
  to,
  icon,
  label,
  count,
  tone,
  activePath,
}: SidebarItemProps) {
  // Active match: if the nav target includes query params, require an
  // exact match (path + search) so /inbox, /inbox?filter=urgent, and
  // /inbox?filter=awaiting each highlight independently. For plain-path
  // targets, require the current location matches the path exactly (or
  // is a child path) AND doesn't carry query params that would match a
  // sibling item instead (e.g. /inbox stays dark when ?filter=urgent).
  const hasQuery = to.includes('?');
  const targetPath = to.split('?')[0] ?? to;
  const [activePart, activeQuery] = activePath.split('?') as [string, string | undefined];
  const active = hasQuery
    ? activePath === to
    : (activePart === targetPath || activePart.startsWith(targetPath + '/')) && !activeQuery;
  return (
    <Link
      to={to}
      className={cn(
        'w-full h-8 px-2 rounded-md text-[13px] flex items-center gap-2 transition-colors focus-ring',
        active
          ? 'bg-ink-700 text-ink-100'
          : 'text-ink-200 hover:bg-ink-800'
      )}
    >
      <span
        className={cn(
          'shrink-0',
          active ? 'text-beacon-300' : 'text-ink-300'
        )}
      >
        {icon}
      </span>
      <span className="truncate flex-1">{label}</span>
      {count != null && (
        <span
          className={cn(
            'mono text-[10.5px] px-1 rounded',
            tone === 'red'
              ? 'bg-danger-500/15 text-danger-400'
              : tone === 'amber'
                ? 'bg-warn-500/15 text-warn-400'
                : active
                  ? 'bg-ink-600 text-ink-100'
                  : 'bg-ink-700 text-ink-300'
          )}
        >
          {count}
        </span>
      )}
    </Link>
  );
}

function SLAPulseCard({
  breaching,
  warn,
  ok,
}: {
  breaching: number;
  warn: number;
  ok: number;
}) {
  return (
    <>
      <div className="text-[10.5px] uppercase tracking-[0.12em] text-ink-300 font-semibold mb-2">
        SLA pulse · today
      </div>
      <div className="bg-ink-800 border border-ink-600 rounded-md p-2.5 space-y-2">
        <PulseRow tone="danger" label="Breaching" count={breaching} />
        <PulseRow tone="warn" label="At risk" count={warn} />
        <PulseRow tone="ok" label="On track" count={ok} />
      </div>
    </>
  );
}

function PulseRow({
  tone,
  label,
  count,
}: {
  tone: 'danger' | 'warn' | 'ok';
  label: string;
  count: number;
}) {
  const dotClass =
    tone === 'danger'
      ? 'bg-danger-500'
      : tone === 'warn'
        ? 'bg-warn-500'
        : 'bg-ok-500';
  const textClass =
    tone === 'danger'
      ? 'text-danger-400'
      : tone === 'warn'
        ? 'text-warn-400'
        : 'text-ok-400';
  return (
    <div className="flex items-center justify-between text-[11.5px]">
      <span className="inline-flex items-center gap-1.5 text-ink-200">
        <span className={cn('w-1.5 h-1.5 rounded-full', dotClass)} />
        {label}
      </span>
      <span className={cn('mono', textClass)}>{count}</span>
    </div>
  );
}
